import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import {
  base_colorAction,
  cta_linkTextAlert,
  cta_linkTextAlertHover,
  notification_colorAlert,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_regular,
  font_size_lg,
} from 'ext-ui/style/typography'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import ManagerReviewRejectionModal from 'src/component-modal/ManagerReviewRejectionModal/ManagerReviewRejectionModal'
import DynamicSuspenseContainer from 'src/components/DynamicSuspenseContainer/DynamicSuspenseContainer'
import FloatingToolbar from 'src/components/FloatingToolbar/FloatingToolbar'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingShimmer from 'src/components/LoadingShimmer/LoadingShimmer'
import SvgClose from 'src/components/Svgs/SvgClose'
import SvgFlag from 'src/components/Svgs/SvgFlag'
import SvgNewCheck from 'src/components/Svgs/SvgNewCheck'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import PluralTrns from 'src/localization/PluralTrns'
import { useTranslate } from 'src/localization/Trns'
import {
  TransactionListModel,
  TransactionManagerReviewStatus,
} from 'src/models/transaction.models'
import { usePatchTransactionManagerReview } from 'src/queries/expenseReview.queries'
import { useSuspenseQueryTransactionsCount } from 'src/queries/transactions.queries'
import {
  selectIsSelectAllTransactionsFromState,
  selectSelectAllTransactionsFiltersFromState,
} from 'src/redux/selectors/data/transactions.selectors'
import { css } from 'styled-components'

interface TransactionsTableNeedsApprovalBulkFloatingToolbarProps {
  selectedTransactions: TransactionListModel[]
  handleCloseToolbar: () => void
  filters?: TransactionsFiltersBody
}

const AllTransactionsSelectedCount: React.FC<{
  source: string
  filters: TransactionsFiltersBody
  expressions?: Record<string, string>
}> = ({ source, filters, expressions }) => {
  const translate = useTranslate()

  const { data: allTransactionsCount } = useSuspenseQueryTransactionsCount({
    filters,
  })

  return translate(source, {
    count: `${allTransactionsCount}`,
    ...expressions,
  })
}

const TransactionsTableNeedsApprovalBulkFloatingToolbar: React.ForwardRefRenderFunction<
  HTMLDivElement,
  TransactionsTableNeedsApprovalBulkFloatingToolbarProps
> = (props, ref) => {
  const translate = useTranslate()

  const { analytics } = useAnalyticsContext()
  const [showManagerReviewRejectionModal, setShowManagerReviewRejectionModal] =
    useState<boolean>(false)

  const selectAllTransactionsFiltersBody =
    useSelector(selectSelectAllTransactionsFiltersFromState) || {}

  const selectedTransactionsCount = props.selectedTransactions.length

  const allSelected = useSelector(selectIsSelectAllTransactionsFromState)

  const { mutate: patchTransactionManagerReview } =
    usePatchTransactionManagerReview()

  return (
    <>
      <FloatingToolbar
        visible={selectedTransactionsCount > 0 || allSelected}
        ref={ref}
        noColumnGap
        noPadding
        css={css`
          > div {
            align-items: stretch;
          }

          .no-shell-no-padding {
            font-family: ${font_family_weight_regular};
            padding: ${spacing(5)};
            border-left: 1px solid ${styleConstants.colors.gray_medium};
            font-size: ${font_size_lg};
            line-height: 18px;
          }

          .flag-btn {
            color: ${cta_linkTextAlert};
            &:hover {
              color: ${cta_linkTextAlertHover};
            }
          }
        `}
      >
        <span
          css={css`
            align-items: center;
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )}
            white-space: nowrap;
            display: inline-flex;
            padding: ${spacing(5)};
          `}
        >
          {!allSelected ? (
            <PluralTrns
              pluralSource={'${count} transactions selected'}
              singleSource={'1 transaction selected'}
              count={selectedTransactionsCount}
            />
          ) : (
            <DynamicSuspenseContainer
              fallback={<LoadingShimmer width={'200px'} height={'24px'} />}
            >
              <AllTransactionsSelectedCount
                source={'${count} transactions selected'}
                filters={selectAllTransactionsFiltersBody}
              />
            </DynamicSuspenseContainer>
          )}
        </span>
        <Button
          variant={'no-shell-no-padding'}
          dropShadow={false}
          iconLeft={
            <SvgNewCheck
              stroke={base_colorAction}
              css={css`
                scale: calc(20 / 16);
              `}
            />
          }
          data-testid={'review-bulk'}
          onClick={(): void => {
            patchTransactionManagerReview({
              body: {
                transactionIds: props.selectedTransactions.map(
                  (transaction) => transaction.id,
                ),
                managerReviewStatus: TransactionManagerReviewStatus.Reviewed,
              },
              filters: props.filters || {},
            })
            analytics?.track('manager review actioned txn', {
              action: 'bulk approved',
            })
          }}
        >
          {translate('Review')}
        </Button>
        <Button
          variant={'no-shell-no-padding'}
          className={'flag-btn'}
          dropShadow={false}
          iconLeft={
            <SvgFlag
              stroke={notification_colorAlert}
              css={css`
                scale: calc(20 / 16);
              `}
            />
          }
          data-testid={'flag-bulk'}
          onClick={(): void => setShowManagerReviewRejectionModal(true)}
        >
          {translate('Reject')}
        </Button>
        <div
          css={css`
            display: flex;
            align-items: center;
            padding: ${spacing(5)};
            border-left: 1px solid ${styleConstants.colors.gray_medium};
          `}
        >
          <HoverableLink
            ariaLabelSource={'Close'}
            onClick={props.handleCloseToolbar}
            testId={'closeBtn'}
          >
            <div
              css={css`
                svg path {
                  stroke: ${themeConstants.colors.font_primary};
                }
                &:hover {
                  svg path {
                    stroke: ${themeConstants.colors.cta_dark};
                  }
                }
                height: 24px;
              `}
            >
              <SvgClose />
            </div>
          </HoverableLink>
        </div>
      </FloatingToolbar>
      <ManagerReviewRejectionModal
        isOpen={showManagerReviewRejectionModal}
        onClose={() => {
          setShowManagerReviewRejectionModal(false)
        }}
        transactionIds={props.selectedTransactions.map(
          (transaction) => transaction.id,
        )}
        filters={props.filters || {}}
      />
    </>
  )
}

export default React.forwardRef(
  TransactionsTableNeedsApprovalBulkFloatingToolbar,
)
